import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Alert,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import { useModalTracking, useTenant } from '../../hooks';
import IntegrationIcon from '@mui/icons-material/Extension';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TuneIcon from '@mui/icons-material/Tune';
import RouteIcon from '@mui/icons-material/Route';
import PlaceIcon from '@mui/icons-material/Place';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import { Tenant, Destination, TenantNegotiationStrategy, MarketplaceNameEnum, PriceSortPolicyLabels, PriceSortPolicy, PluginConfig } from '../../models/tenant';
import ScoringTab from './ScoringTab';
import { CountryCode, COUNTRY_NAMES } from '../../models/country';
import { COUNTRY_TO_FLAG } from '../../utils/languageUtils';
import { apiGet, apiPost, showErrorToast } from '../../services/apiClient';
import TextInput from '../inputs/textInput';
import { I18nLang, UserPlatformSubRole, UserPlatformRole } from '../../models/user';
import { toast } from '../../utils/toast';
import { IMAPSource, SMTPSource } from '../../models/email-service';
import { AutoSearchMode } from '../../models/vehicleAutoSearch';
import EmailTab from './EmailTab';
import DefaultLoadingSheetTemplate, { OrdemTransporteTemplate } from '../templates/DefaultLoadingSheetTemplate';
import { useTranslation } from 'react-i18next';
import { SelectInput } from '../inputs';
import { Description, Email } from '@mui/icons-material';
import { StandardDialog } from '../dialogs/base';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { createBillingPortalLink, createBillingSubscribeLink, syncSubscription } from '../../services/subscriptionService';
import TeamManagement from './teamManagement';
import { AuthContext } from '../../context/authContext';
import DestinationsTab from './DestinationsTab/DestinationsTab';

type TenantFormState = Omit<Tenant, 'id' | 'updatedOn' | 'createdAt'>

const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES).map(([code, name]) => ({
  code: code as CountryCode,
  name,
  flag: COUNTRY_TO_FLAG[code as CountryCode] || ''
}));

const validateRegCountry = (value: string, t: (key: string) => string): string | null => {
  const countryKeys = Object.keys(COUNTRY_NAMES);
  if (!countryKeys.includes(value)) {
    return t('Please select a valid country from the dropdown');
  }
  return null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: (open: boolean) => void;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  if (value !== index) { // Only render active tab
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </div>
  );
};

const TenantSettingsModal: React.FC<Props> = ({ open, onClose, setOpen, initialTab }) => {
  // Track modal open/close
  useModalTracking('tenant_settings', open, { initial_tab: initialTab ?? 0 });

  const { t } = useTranslation();
  const authContext = useContext(AuthContext)!;
  const { tenant: contextTenant, refresh: refreshTenant } = useTenant();

  const [activeTab, setActiveTab] = useState(initialTab ?? 0);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [whitelistedSignupDomainsInput, setWhitelistedSignupDomainsInput] = useState('');
  const [whitelistedSignupDomainsError, setWhitelistedSignupDomainsError] = useState('');
  const DOMAIN_REGEX = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    general?: boolean;
    autoSearch?: boolean;
    autoNego?: boolean;
    mcp?: boolean;
  }>({});
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);

  // Get user's subrole for access control
  const userSubRole = authContext.user?.subRole;
  const isDirector = userSubRole === UserPlatformSubRole.DIRECTOR;
  const isManager = userSubRole === UserPlatformSubRole.TRUCK_MANAGER;

  // Drivers, Reviewers and None cannot see tenant settings; only Managers/Directors
  const canAccessTenantSettings = isDirector || isManager;
  const isAdmin = authContext.user?.role === UserPlatformRole.ADMIN;

  // Tab configuration with visibility rules
  const tabsConfig = [
    { icon: BusinessIcon, label: t('Company'), visible: canAccessTenantSettings },
    { icon: GroupIcon, label: t('Team'), visible: isDirector, disabled: !isDirector },
    { icon: PlaceIcon, label: t('Destinations'), visible: canAccessTenantSettings },
    { icon: RouteIcon, label: t('Routes'), visible: canAccessTenantSettings },
    { icon: PublicIcon, label: t('Countries'), visible: canAccessTenantSettings },
    { icon: TuneIcon, label: t('Scoring'), visible: canAccessTenantSettings },
    { icon: HandshakeIcon, label: t('Negotiation Strategies'), visible: canAccessTenantSettings },
    { icon: Email, label: t('Email'), visible: canAccessTenantSettings },
    { icon: Description, label: t('Email Templates'), visible: canAccessTenantSettings },
    { icon: NotificationsIcon, label: t('Notifications'), visible: canAccessTenantSettings },
    { icon: IntegrationIcon, label: t('Integrations'), visible: canAccessTenantSettings },
    { icon: DescriptionIcon, label: t('Loading Sheet Template'), visible: canAccessTenantSettings },
    { icon: CreditCardIcon, label: t('Subscription'), visible: canAccessTenantSettings, disabled: !subscriptionsEnabled },
  ];

  // Filter visible tabs
  const visibleTabs = tabsConfig.filter(tab => tab.visible !== false);

  // Update active tab when initialTab prop changes
  useEffect(() => {
    if (initialTab !== undefined) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [form, setForm] = useState<TenantFormState>({
    name: '',
    brandingName: '',
    brandingLogoUrl: '',
    apiRateLimitPerMinute: 60,
    defaultLanguage: 1,
    timezoneOffset: 0,
    notificationEmail: '',
    allowUserSignup: false,
    dataRetentionDays: 30,
    isActive: true,
    regCountry: CountryCode.DE,
    awsSesAccessKeyId: '',
    awsSesAccessKeySecret: '',
    awsSesLimitPerSecond: 1,
    workingEmail: '',
    imapHost: '',
    imapPort: 993,
    imapUsername: '',
    imapPassword: '',
    pluginConfig: {},
    pricePerKm: 0.0,
    minLengthMForFullTruckLoad: 0,
    maxLoadingDaysAhead: 3,
    allowAutoValidate: false,
    includePlatesOnAutoValidate: false,
    destinationsToExclude: [{ country: CountryCode.LT, codes: ['VNO'] }],
    preferredDestinations: {
      DEFAULT: [{ country: CountryCode.DE, codes: [''], score: 0 }]
    },
    returnDestinations: {
      DEFAULT: [{ country: CountryCode.DE, codes: [''], score: 0 }]
    },
    preferredRoutes: {},
    targetPriceMarginFactor: 1.1,
    potentialOffersRadiusKm: 200,
    scoringWeights: {},
    language: I18nLang.EN,
    imapForwardingAddress: '',
    pricePremiumFactorPerWeekday: {},
    pricePremiumFactorPerWeekOfYear: {},
    autoRespondForAssemblageOffers: false,
    autoSearchForDelayedTrucks: false,
    drivingHours: {},
    startNegoSamples: [],
    acceptNegoSamples: [],
    scoreRecomputeMinutes: 0,
    workingEmailAlias: '',
    workingEmailPassword: '',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    workingEmailSignaturePlain: '',
    workingEmailSignatureHtml: '',
    emailAccessToken: '',
    emailRefreshToken: '',
    emailTokenExpiresOn: new Date(),
    smtpSource: SMTPSource.NONE,
    imapSource: IMAPSource.NONE,
    imapErrorCount: 0,
    runAIFindMatching: false,
    preferredAutoSearchMode: AutoSearchMode.CIRCLE_TRIP,
    whitelistedDomains: [],
    blacklistedDomains: [],
    whitelistedSignupDomains: [],
    scoredDomains: {},
    workingCountries: [],
    activeSubscriptions: {},
    stripeCustomerId: '',
    negotiationStrategies: [],
    enabledMarketplaces: [],
    bookingFileHtmlTemplate: '',
    priceSortPolicy: PriceSortPolicy.BY_ESTIMATED_PRICE,
    unreachableBookingsParams: {
      enabled: false,
      autoWarnManager: false,
      autoSearch: false
    }
  });

  useEffect(() => {
    const initializeFormData = async () => {
      if (!open || !contextTenant) return;

      // Check if subscriptions are enabled from server info
      const serverInfo = authContext.user?.server;
      setSubscriptionsEnabled(serverInfo?.subscriptionsEnabled || false);

      try {
        const tenantData = contextTenant;
        setTenantId(tenantData.id || null);
        // Sync subscription status
        try {
          const subscriptionData = await syncSubscription(tenantData.id!);
          setSubscriptionStatus(subscriptionData.result || {});
        } catch (error) {
          console.error('Failed to sync subscription:', error);
          setSubscriptionStatus({});
        }

        setForm({
          name: tenantData.name || '',
          brandingName: tenantData.brandingName || '',
          brandingLogoUrl: tenantData.brandingLogoUrl || '',
          apiRateLimitPerMinute: tenantData.apiRateLimitPerMinute || 60,
          defaultLanguage: tenantData.defaultLanguage || 1,
          timezoneOffset: tenantData.timezoneOffset || 0,
          notificationEmail: tenantData.notificationEmail || '',
          allowUserSignup: tenantData.allowUserSignup || false,
          dataRetentionDays: tenantData.dataRetentionDays || 30,
          isActive: tenantData.isActive ?? true,
          regCountry: (tenantData as any).regCountry || CountryCode.DE,
          awsSesAccessKeyId: tenantData.awsSesAccessKeyId || '',
          awsSesAccessKeySecret: tenantData.awsSesAccessKeySecret !== undefined ? tenantData.awsSesAccessKeySecret : '',
          awsSesLimitPerSecond: tenantData.awsSesLimitPerSecond || 1,
          workingEmail: tenantData.workingEmail || '',
          imapHost: tenantData.imapHost || '',
          imapPort: tenantData.imapPort || 993,
          imapUsername: tenantData.imapUsername || '',
          imapPassword: tenantData.imapPassword !== undefined ? tenantData.imapPassword : '',
          pluginConfig: tenantData.pluginConfig || {},
          pricePerKm: tenantData.pricePerKm || 0.0,
          minLengthMForFullTruckLoad: tenantData.minLengthMForFullTruckLoad || 0,
          maxLoadingDaysAhead: tenantData.maxLoadingDaysAhead || 3,
          allowAutoValidate: tenantData.allowAutoValidate || false,
          includePlatesOnAutoValidate: tenantData.includePlatesOnAutoValidate || false,
          destinationsToExclude: tenantData.destinationsToExclude || [{ country: CountryCode.LT, codes: ['VNO'] }],
          preferredDestinations: tenantData.preferredDestinations || {
            DEFAULT: [{ country: CountryCode.DE, codes: [''], score: 0 }]
          },
          returnDestinations: tenantData.returnDestinations || {
            DEFAULT: [{ country: CountryCode.DE, codes: [''], score: 0 }]
          },
          preferredRoutes: tenantData.preferredRoutes || {},
          targetPriceMarginFactor: tenantData.targetPriceMarginFactor || 1.0,
          potentialOffersRadiusKm: tenantData.potentialOffersRadiusKm || 0,
          scoringWeights: tenantData.scoringWeights || {},
          imapForwardingAddress: tenantData.imapForwardingAddress || '',
          pricePremiumFactorPerWeekday: tenantData.pricePremiumFactorPerWeekday || {},
          pricePremiumFactorPerWeekOfYear: tenantData.pricePremiumFactorPerWeekOfYear || {},
          autoRespondForAssemblageOffers: tenantData.autoRespondForAssemblageOffers || false,
          autoSearchForDelayedTrucks: tenantData.autoSearchForDelayedTrucks || false,
          drivingHours: tenantData.drivingHours || {},
          startNegoSamples: tenantData.startNegoSamples || [],
          acceptNegoSamples: tenantData.acceptNegoSamples || [],
          scoreRecomputeMinutes: tenantData.scoreRecomputeMinutes || 0,
          workingEmailAlias: tenantData.workingEmailAlias || '',
          workingEmailPassword: tenantData.workingEmailPassword !== undefined ? tenantData.workingEmailPassword : '',
          smtpHost: tenantData.smtpHost || '',
          smtpPort: tenantData.smtpPort || 587,
          smtpSecure: tenantData.smtpSecure || false,
          workingEmailSignaturePlain: tenantData.workingEmailSignaturePlain || '',
          workingEmailSignatureHtml: tenantData.workingEmailSignatureHtml || '',
          emailAccessToken: tenantData.emailAccessToken !== undefined ? tenantData.emailAccessToken : '',
          emailRefreshToken: tenantData.emailRefreshToken !== undefined ? tenantData.emailRefreshToken : '',
          emailTokenExpiresOn: tenantData.emailTokenExpiresOn || new Date(),
          smtpSource: tenantData.smtpSource !== undefined ? tenantData.smtpSource : SMTPSource.NONE,
          imapSource: tenantData.imapSource !== undefined ? tenantData.imapSource : IMAPSource.NONE,
          imapErrorCount: tenantData.imapErrorCount || 0,
          runAIFindMatching: tenantData.runAIFindMatching || false,
          preferredAutoSearchMode: AutoSearchMode.CIRCLE_TRIP,
          whitelistedDomains: tenantData.whitelistedDomains || [],
          blacklistedDomains: tenantData.blacklistedDomains || [],
          whitelistedSignupDomains: tenantData.whitelistedSignupDomains || [],
          scoredDomains: tenantData.scoredDomains || {},
          language: tenantData.language || I18nLang.EN,
          workingCountries: tenantData.workingCountries || [],
          activeSubscriptions: tenantData.activeSubscriptions || {},
          stripeCustomerId: tenantData.stripeCustomerId || '',
          negotiationStrategies: tenantData.negotiationStrategies || [],
          enabledMarketplaces: tenantData.enabledMarketplaces || [],
          bookingFileHtmlTemplate: tenantData.bookingFileHtmlTemplate || '',
          priceSortPolicy: tenantData.priceSortPolicy || PriceSortPolicy.BY_ESTIMATED_PRICE,
          unreachableBookingsParams: tenantData.unreachableBookingsParams || {
            enabled: false,
            autoWarnManager: false,
            autoSearch: false
          }
        });
      } catch {
        showErrorToast(undefined, t('Error fetching tenant data'));
        setOpen(false);
      }
    };
    initializeFormData();
  }, [open, contextTenant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    let parsedValue: any = value;
    // Handle different input types
    if (type === 'number') {
      parsedValue = Number(value);
    } else if (type === 'checkbox') {
      parsedValue = e.target.checked;
    } else {
      // Handle boolean fields that are rendered as text inputs
      const currentFieldValue = form[name as keyof TenantFormState];
      if (typeof currentFieldValue === 'boolean') {
        parsedValue = value.toLowerCase() === 'true';
      }
    }
    setForm(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handlePluginConfigChange = <K extends keyof PluginConfig>(plugin: K, field: string, value: string) => {
    setForm(prev => {
      const existing = prev.pluginConfig?.[plugin] || {};
      return {
        ...prev,
        pluginConfig: {
          ...prev.pluginConfig,
          [plugin]: { ...existing, [field]: value }
        }
      };
    });
  };

  // Handlers for Preferred Routes
  const handleRouteChange = (
    routeKey: string,
    destinationIndex: number,
    key: string,
    value: any
  ) => {
    const destinations = [...form.preferredRoutes[routeKey]];
    destinations[destinationIndex] = {
      ...destinations[destinationIndex],
      [key]: value
    };
    setForm(prev => ({
      ...prev,
      preferredRoutes: {
        ...prev.preferredRoutes,
        [routeKey]: destinations
      }
    }));
  };

  const handleAddPreferredRoute = () => {
    const newKey = `ROUTE_${Object.keys(form.preferredRoutes).length + 1}`;
    setForm(prev => ({
      ...prev,
      preferredRoutes: {
        ...prev.preferredRoutes,
        [newKey]: [{ country: CountryCode.DE, codes: [''], stepsAhead: 1 }]
      }
    }));
  };
  const handleAddDestinationToRoute = (routeKey: string) => {
    setForm(prev => ({
      ...prev,
      preferredRoutes: {
        ...prev.preferredRoutes,
        [routeKey]: [
          ...prev.preferredRoutes[routeKey],
          { country: CountryCode.DE, codes: [''], stepsAhead: 1 }
        ]
      }
    }));
  };

  const handleRemoveDestinationFromRoute = (routeKey: string, destinationIndex: number) => {
    const destinations = form.preferredRoutes[routeKey].filter((_, index) => index !== destinationIndex);
    if (destinations.length === 0) {
      // If no destinations left, remove the entire route
      const updated = { ...form.preferredRoutes };
      delete updated[routeKey];
      setForm(prev => ({ ...prev, preferredRoutes: updated }));
    } else {
      setForm(prev => ({
        ...prev,
        preferredRoutes: {
          ...prev.preferredRoutes,
          [routeKey]: destinations
        }
      }));
    }
  };

  const handleRemovePreferredRoute = (routeKey: string) => {
    const updated = { ...form.preferredRoutes };
    delete updated[routeKey];
    setForm(prev => ({ ...prev, preferredRoutes: updated }));
  };

  const handleRouteKeyChange = (oldKey: string, newKey: string) => {
    if (oldKey === newKey || form.preferredRoutes[newKey]) return;
    const updated: Record<string, Destination[]> = {}; // Preserve order
    Object.entries(form.preferredRoutes).forEach(([k, v]) => {
      if (k === oldKey) {
        updated[newKey] = v;
      } else {
        updated[k] = v;
      }
    });
    setForm(prev => ({ ...prev, preferredRoutes: updated }));
  };

  const moveDestinationInRoute = (routeKey: string, fromIndex: number, toIndex: number) => {
    const destinations = [...form.preferredRoutes[routeKey]];
    const [movedItem] = destinations.splice(fromIndex, 1);
    destinations.splice(toIndex, 0, movedItem);

    setForm(prev => ({
      ...prev,
      preferredRoutes: {
        ...prev.preferredRoutes,
        [routeKey]: destinations
      }
    }));
  };

  const handleScoringWeightsChange = React.useCallback((newWeights: Record<string, any>) => {
    setForm(prev => ({
      ...prev,
      scoringWeights: newWeights
    }));
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderPlaceholderTab = (title: string, description: string) => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h5" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );

  const linkMail = async (mail: string) => {
    try {
      const response = await apiGet(process.env.REACT_APP_API_URL + `/api/v1/tenant/${tenantId}/${mail}/auth_url`);
      const data = await response.json();
      if (data.result) {
        localStorage.setItem('pendingTenantMailProvider', mail);
        window.open(data.result, '_self');
      } else {
        showErrorToast(undefined, t('Failed to get authentication URL'));
      }
    } catch (error) {
      showErrorToast(error, t('Failed to initiate authentication'));
    }
  };

  const handleSubmit = async () => {
    if (!tenantId) {
      return;
    }
    const regCountryError = validateRegCountry(form.regCountry, t);
    if (regCountryError) {
      return;
    }
    try {
      const payload = {
        ...form,
        preferredDestinations: form.preferredDestinations,
        returnDestinations: form.returnDestinations,
        preferredRoutes: form.preferredRoutes,
        scoringWeights: form.scoringWeights,
        notifications_opt_outs: {},
        unreachableBookingsParams: form.unreachableBookingsParams
      };

      // Strip null values (null = "configured, don't change") from top-level and pluginConfig
      Object.keys(payload).forEach(key => {
        if (payload[key as keyof typeof payload] === null) {
          delete (payload as Record<string, unknown>)[key];
        }
      });
      if (payload.pluginConfig && typeof payload.pluginConfig === 'object') {
        for (const pluginConfig of Object.values(payload.pluginConfig)) {
          if (pluginConfig && typeof pluginConfig === 'object') {
            for (const [field, value] of Object.entries(pluginConfig)) {
              if (value === null) {
                delete (pluginConfig as Record<string, unknown>)[field];
              }
            }
          }
        }
      }

      await apiPost(process.env.REACT_APP_API_URL + `/api/v1/tenant/${tenantId}/update`, payload);
      await refreshTenant(); // Refresh tenant data in context after update
      onClose();
      toast.success(t('Company updated successfully'));
    } catch {
      showErrorToast(undefined, t('Error updating company'));
    }
  };

  // If user doesn't have access to tenant settings, show message
  if (!canAccessTenantSettings) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title={t('Settings Unavailable')}
        maxWidth="sm"
        primaryAction={{
          label: t('Close'),
          onClick: onClose,
        }}
      >
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('Access Denied')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('You do not have permission to access tenant settings. Please contact your administrator.')}
          </Typography>
        </Box>
      </StandardDialog>
    );
  }

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={t('Company Settings')}
      maxWidth="lg"
      fullHeight={true}
      height={{ xs: '95vh', sm: '90vh', md: '85vh', lg: '80vh' }}
      primaryAction={{
        label: t('Save'),
        onClick: handleSubmit,
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 3,
            '& .MuiTab-root': {
              color: 'text.grey',
              '&.Mui-selected': {
                color: 'text.darkgrey',
                fontWeight: 600,
              },
              '&:hover': {
                color: 'text.darkGrey',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'background.shadowBlack',
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={index}
              icon={<tab.icon />}
              label={tab.label}
              iconPosition="start"
              sx={{ minHeight: 64, textTransform: 'none', fontWeight: 500 }}
              disableRipple
              disabled={tab.disabled}
            />
          ))}
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Company Information')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Manage your company details and configuration settings')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Box>
            <Autocomplete
              options={COUNTRY_OPTIONS}
              getOptionLabel={(option) => option.name}
              value={COUNTRY_OPTIONS.find(c => c.code === form.regCountry) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setForm(prev => ({ ...prev, regCountry: newValue.code }));
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{option.flag}</span>
                  <span>{option.code} - {option.name}</span>
                </Box>
              )}
              slotProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                    },
                  ],
                },
              }}
              renderInput={(params) => {
                const selectedOption = COUNTRY_OPTIONS.find(c => c.code === form.regCountry);
                return (
                  <TextInput
                    {...params}
                    label={t('Registration Country')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption?.flag ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <span>{selectedOption.flag}</span>
                        </Box>
                      ) : null,
                    }}
                  />
                );
              }}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Company Name')}
              name="name"
              value={form.name}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Branding Name')}
              name="brandingName"
              value={form.brandingName}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Branding Logo URL')}
              name="brandingLogoUrl"
              value={form.brandingLogoUrl}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Notification Email')}
              name="notificationEmail"
              type="email"
              value={form.notificationEmail}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Working Email')}
              name="workingEmail"
              type="email"
              value={form.workingEmail}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('API Rate Limit (per minute)')}
              name="apiRateLimitPerMinute"
              type="number"
              value={form.apiRateLimitPerMinute}
              onChange={handleChange}
              disabled
              helperText={t('Maximum API requests per minute (managed by system administrator)')}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Data Retention (days)')}
              name="dataRetentionDays"
              type="number"
              value={form.dataRetentionDays}
              onChange={handleChange}
              disabled
              helperText={t('Number of days to retain data (managed by system administrator)')}
            />
          </Box>

          <Box>
            <SelectInput
              fullWidth
              label={t('Timezone Offset (hours)')}
              name="timezoneOffset"
              value={String(form.timezoneOffset / 60)}
              onChange={(e) => {
                const hours = parseFloat(e.target.value as string) || 0;
                setForm(prev => ({ ...prev, timezoneOffset: Math.round(hours * 60) }));
              }}
              options={[
                { key: 'UTC-12:00', value: '-12' },
                { key: 'UTC-11:00', value: '-11' },
                { key: 'UTC-10:00', value: '-10' },
                { key: 'UTC-09:30', value: '-9.5' },
                { key: 'UTC-09:00', value: '-9' },
                { key: 'UTC-08:00', value: '-8' },
                { key: 'UTC-07:00', value: '-7' },
                { key: 'UTC-06:00', value: '-6' },
                { key: 'UTC-05:00 (EST)', value: '-5' },
                { key: 'UTC-04:00', value: '-4' },
                { key: 'UTC-03:30', value: '-3.5' },
                { key: 'UTC-03:00', value: '-3' },
                { key: 'UTC-02:00', value: '-2' },
                { key: 'UTC-01:00', value: '-1' },
                { key: 'UTC±00:00 (GMT)', value: '0' },
                { key: 'UTC+01:00 (CET)', value: '1' },
                { key: 'UTC+02:00 (EET)', value: '2' },
                { key: 'UTC+03:00', value: '3' },
                { key: 'UTC+03:30', value: '3.5' },
                { key: 'UTC+04:00', value: '4' },
                { key: 'UTC+04:30', value: '4.5' },
                { key: 'UTC+05:00', value: '5' },
                { key: 'UTC+05:30 (IST)', value: '5.5' },
                { key: 'UTC+05:45', value: '5.75' },
                { key: 'UTC+06:00', value: '6' },
                { key: 'UTC+06:30', value: '6.5' },
                { key: 'UTC+07:00', value: '7' },
                { key: 'UTC+08:00', value: '8' },
                { key: 'UTC+08:45', value: '8.75' },
                { key: 'UTC+09:00', value: '9' },
                { key: 'UTC+09:30', value: '9.5' },
                { key: 'UTC+10:00', value: '10' },
                { key: 'UTC+10:30', value: '10.5' },
                { key: 'UTC+11:00', value: '11' },
                { key: 'UTC+12:00', value: '12' },
                { key: 'UTC+12:45', value: '12.75' },
                { key: 'UTC+13:00', value: '13' },
                { key: 'UTC+14:00', value: '14' },
              ]}
              helperText={t('Select your timezone offset from UTC')}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Price Per KM')}
              name="pricePerKm"
              type="number"
              value={form.pricePerKm}
              onChange={handleChange}
            />
          </Box>

          <Box>
            <SelectInput
              fullWidth
              label={t('Price sort policy')}
              name="priceSortPolicy"
              value={String(form.priceSortPolicy)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                event.target.type = 'number';
                return handleChange(event);
              }}
              options={Object.entries(PriceSortPolicyLabels).map(([value, label]) => ({
                key: t(label),
                value: String(value),
              }))}
              renderValue={(selected: string) => PriceSortPolicyLabels[Number(selected)]}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Target Price Margin Factor')}
              name="targetPriceMarginFactor"
              type="number"
              slotProps={{ htmlInput: { step: '0.01', min: 1 } }}
              value={form.targetPriceMarginFactor}
              onChange={handleChange}
              helperText={t('Multiplier applied to target price (e.g., 1.0 = no margin, 1.2 = +20%)')}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Potential Offers Radius (km)')}
              name="potentialOffersRadiusKm"
              type="number"
              slotProps={{ htmlInput: { step: '1', min: 0 } }}
              value={form.potentialOffersRadiusKm}
              onChange={handleChange}
              helperText={t('Search radius in kilometers for potential offers')}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Minimum Length (m) for FTL')}
              name="minLengthMForFullTruckLoad"
              type="number"
              slotProps={{ htmlInput: { step: '1', min: 0 } }}
              value={form.minLengthMForFullTruckLoad}
              onChange={handleChange}
              helperText={t('Minimum load length in meters to qualify as a full truck load')}
            />
          </Box>

          <Box>
            <TextInput
              fullWidth
              label={t('Max Loading Days Ahead')}
              name="maxLoadingDaysAhead"
              type="number"
              slotProps={{ htmlInput: { step: '1', min: 1 } }}
              value={form.maxLoadingDaysAhead}
              onChange={handleChange}
              helperText={t('Maximum number of days ahead for auto-search loading date range')}
            />
          </Box>

          <Box>
            <Autocomplete
              multiple
              freeSolo
              fullWidth
              size="small"
              disabled={!isAdmin}
              options={[]}
              value={form.whitelistedSignupDomains || []}
              inputValue={whitelistedSignupDomainsInput}
              onInputChange={(_, newInputValue) => {
                setWhitelistedSignupDomainsInput(newInputValue);
                setWhitelistedSignupDomainsError('');
              }}
              onChange={(_, newValue) => {
                const last = newValue[newValue.length - 1];
                if (last !== undefined && !DOMAIN_REGEX.test(last)) {
                  setWhitelistedSignupDomainsError(t('Enter a valid domain (e.g. example.com)'));
                  return;
                }
                setWhitelistedSignupDomainsError('');
                setForm(prev => ({ ...prev, whitelistedSignupDomains: newValue }));
              }}
              onBlur={() => {
                const trimmed = whitelistedSignupDomainsInput.trim();
                if (trimmed) {
                  if (!DOMAIN_REGEX.test(trimmed)) {
                    setWhitelistedSignupDomainsError(t('Enter a valid domain (e.g. example.com)'));
                    return;
                  }
                  if (!(form.whitelistedSignupDomains || []).includes(trimmed)) {
                    setForm(prev => ({ ...prev, whitelistedSignupDomains: [...(prev.whitelistedSignupDomains || []), trimmed] }));
                  }
                }
                setWhitelistedSignupDomainsError('');
                setWhitelistedSignupDomainsInput('');
              }}
              renderTags={(value: string[], getTagProps) => value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
              ))
              }
              renderInput={(params) => (
                <TextInput
                  {...params}
                  label={t('Whitelisted Signup Domains')}
                  placeholder={t('example.com')}
                  error={!!whitelistedSignupDomainsError}
                  helperText={whitelistedSignupDomainsError || t('Email domains allowed to self-register and join this tenant')}
                />
              )}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.allowUserSignup}
                  onChange={(e) => setForm(prev => ({ ...prev, allowUserSignup: e.target.checked }))}
                  disabled={!isAdmin}
                />
              }
              label={t('Allow User Signup')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              {t('Allow new users to sign up (managed by system administrator)')}
            </Typography>
          </Box>

          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  disabled
                />
              }
              label={t('Company Active')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              {t('Company account status (managed by system administrator)')}
            </Typography>
          </Box>

          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.allowAutoValidate}
                  onChange={(e) => setForm(prev => ({ ...prev, allowAutoValidate: e.target.checked }))}
                />
              }
              label={t('Allow Auto Validation')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              {t('Automatically validate and accept bookings when certain criteria are met without manual confirmation')}
            </Typography>
          </Box>

          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.includePlatesOnAutoValidate}
                  onChange={(e) => setForm(prev => ({ ...prev, includePlatesOnAutoValidate: e.target.checked }))}
                  disabled={!form.allowAutoValidate}
                />
              }
              label={t('Include Plate Numbers on Auto Validation')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              {t('Automatically append truck and trailer plate numbers to acceptance emails when auto-validating negotiations')}
            </Typography>
          </Box>

          {/* Unreachable Bookings Params Controls */}
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {t('Unreachable Bookings Settings')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.unreachableBookingsParams?.enabled || false}
                  onChange={e => setForm(prev => ({
                    ...prev,
                    unreachableBookingsParams: {
                      enabled: e.target.checked,
                      autoWarnManager: typeof prev.unreachableBookingsParams?.autoWarnManager === 'boolean' ? prev.unreachableBookingsParams.autoWarnManager : false,
                      autoSearch: typeof prev.unreachableBookingsParams?.autoSearch === 'boolean' ? prev.unreachableBookingsParams.autoSearch : false
                    }
                  }))}
                />
              }
              label={t('Enable unreachable bookings handling')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1, mb: 1 }}>
              {t('If enabled, the system will handle bookings that cannot be reached according to the configured options below.')}
            </Typography>
            <Box sx={{ ml: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.unreachableBookingsParams?.autoWarnManager || false}
                    onChange={e => setForm(prev => ({
                      ...prev,
                      unreachableBookingsParams: {
                        enabled: typeof prev.unreachableBookingsParams?.enabled === 'boolean' ? prev.unreachableBookingsParams.enabled : false,
                        autoWarnManager: e.target.checked,
                        autoSearch: typeof prev.unreachableBookingsParams?.autoSearch === 'boolean' ? prev.unreachableBookingsParams.autoSearch : false
                      }
                    }))}
                    disabled={!form.unreachableBookingsParams?.enabled}
                  />
                }
                label={t('Auto-warn manager when booking unreachable')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.unreachableBookingsParams?.autoSearch || false}
                    onChange={e => setForm(prev => ({
                      ...prev,
                      unreachableBookingsParams: {
                        enabled: typeof prev.unreachableBookingsParams?.enabled === 'boolean' ? prev.unreachableBookingsParams.enabled : false,
                        autoWarnManager: typeof prev.unreachableBookingsParams?.autoWarnManager === 'boolean' ? prev.unreachableBookingsParams.autoWarnManager : false,
                        autoSearch: e.target.checked
                      }
                    }))}
                    disabled={!form.unreachableBookingsParams?.enabled}
                  />
                }
                label={t('Auto-search for alternatives when booking unreachable')}
              />
            </Box>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {tenantId && authContext.user?.subRole === UserPlatformSubRole.DIRECTOR ? (
          <TeamManagement tenantId={tenantId} />
        ) : (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('Team Management Unavailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Only directors can access team management features.')}
            </Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Circle Route Settings (Circle Trip Mode Only)')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Configure sequential route preferences for')} <strong>{t('Auto Search Mode - Circle Trip')}</strong>. {t('These circle routes define the journey path where your vehicle follows a specific sequence of destinations. This tab is')} <strong>{t('only used when Circle Trip mode is selected')}</strong>, {t('not for Preferred Destinations mode.')}
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            {t('Circle Routes')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Define sequential routes where each destination represents a step in your circular journey. The system will search for offers that match the next destination(s) in your route sequence.')}
            {t('Use')} <strong>{t('Steps Ahead')}</strong> {t('to control search flexibility: 1 = next destination only, higher values = search further ahead in the route.')}
          </Typography>

          {Object.entries(form.preferredRoutes).map(([routeKey, destinations], routeIndex) => (
            <Card key={`route-${routeIndex}`} sx={{ mb: 3, border: '1px solid border.default' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    label={t('Route Key')}
                    value={routeKey}
                    onChange={e => handleRouteKeyChange(routeKey, e.target.value)}
                    sx={{ minWidth: 300, mr: 2 }}
                    size="small"
                  />
                  <Chip
                    label={`${destinations.length} step${destinations.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      color: 'text.darkgrey',
                      backgroundColor: 'background.grey',
                      border: '1px solid border.default',
                      '&:hover': {
                        backgroundColor: 'background.darkGrey',
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />
                {destinations.map((destination, destIndex) => (
                  <Box
                    key={destIndex}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'center',
                      p: 2,
                      backgroundColor: 'background.white',
                      borderRadius: 1,
                      border: '1px solid border.default'
                    }}
                  >
                    <Chip
                      label={
                        destination?.stepsAhead !== undefined && destination?.stepsAhead > 1
                          ? `Step ${destIndex + 1} (+${destination.stepsAhead})`
                          : `Step ${destIndex + 1}`
                      }
                      size="small"
                      sx={{
                        color: 'text.white',
                        backgroundColor: destination?.stepsAhead !== undefined && destination?.stepsAhead > 1 ? 'background.blue' : 'background.shadowBlack',
                        fontWeight: 600,
                        minWidth: 90,
                      }}
                    />
                    <Autocomplete
                      value={COUNTRY_OPTIONS.find(c => c.code === destination?.country) || null}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          handleRouteChange(routeKey, destIndex, 'country', newValue.code as CountryCode);
                        }
                      }}
                      options={COUNTRY_OPTIONS}
                      getOptionLabel={(option) => `${option.code} - ${option.name}`}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{option.flag}</span>
                          <span>{option.code} - {option.name}</span>
                        </Box>
                      )}
                      slotProps={{
                        popper: {
                          placement: 'bottom-start',
                          modifiers: [
                            {
                              name: 'flip',
                              enabled: false,
                            },
                            {
                              name: 'preventOverflow',
                              enabled: true,
                            },
                          ],
                        },
                      }}
                      renderInput={(params) => {
                        const selectedOption = COUNTRY_OPTIONS.find(c => c.code === destination?.country);
                        return (
                          <TextField
                            {...params}
                            label={t('Country')}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: selectedOption?.flag ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                  <span>{selectedOption.flag}</span>
                                </Box>
                              ) : null,
                            }}
                          />
                        );
                      }}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      sx={{ minWidth: 200 }}
                      size="small"
                    />
                    <TextField
                      label={t('Postal Codes')}
                      placeholder="20, 40, 10"
                      value={destination?.codes?.join(',') || ''}
                      onChange={e => handleRouteChange(routeKey, destIndex, 'codes', e.target.value.split(','))}
                      onBlur={e => handleRouteChange(routeKey, destIndex, 'codes', e.target.value.split(',').map(code => code.trim()).filter(code => code.length > 0))}
                      sx={{ flex: 1 }}
                      size="small"
                    />
                    <Tooltip title={t('How many steps ahead to search for offers. 1 = next destination only, higher values = search further ahead.')}>
                      <TextField
                        label={t('Steps Ahead')}
                        type="number"
                        value={destination?.stepsAhead ?? ''}
                        onChange={e => {
                          const value = e.target.value;
                          handleRouteChange(routeKey, destIndex, 'stepsAhead',
                            value === '' ? 1 : Math.max(1, parseInt(value) || 1)
                          );
                        }}
                        placeholder="1"
                        slotProps={{ htmlInput: { min: 1, max: 10 } }}
                        sx={{ minWidth: 130 }}
                        size="small"
                      />
                    </Tooltip>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('Move up')}>
                        <span>
                          <IconButton
                            onClick={() => moveDestinationInRoute(routeKey, destIndex, destIndex - 1)}
                            disabled={destIndex === 0}
                            size="small"
                            disableRipple
                            sx={{
                              color: 'text.darkgrey',
                              '&:hover': {
                                backgroundColor: 'background.darkGrey',
                              },
                              '&.Mui-disabled': {
                                color: 'text.light',
                              }
                            }}
                          >
                            <ArrowUpwardIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('Move down')}>
                        <span>
                          <IconButton
                            onClick={() => moveDestinationInRoute(routeKey, destIndex, destIndex + 1)}
                            disabled={destIndex === destinations.length - 1}
                            size="small"
                            disableRipple
                            sx={{
                              color: 'text.darkgrey',
                              '&:hover': {
                                backgroundColor: 'background.darkGrey',
                              },
                              '&.Mui-disabled': {
                                color: 'text.light',
                              }
                            }}
                          >
                            <ArrowDownwardIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('Remove step')}>
                        <IconButton
                          onClick={() => handleRemoveDestinationFromRoute(routeKey, destIndex)}
                          color="error"
                          size="small"
                          disableRipple
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => handleAddDestinationToRoute(routeKey)}
                  startIcon={<AddIcon />}
                  variant="outlined"
                  disableRipple
                  sx={{
                    color: 'text.darkgrey',
                    borderColor: 'border.default',
                    '&:hover': {
                      backgroundColor: 'background.shadowBlack',
                      color: 'text.white',
                      borderColor: 'border.default',
                    },
                  }}
                >
                  {t('Add Step')}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemovePreferredRoute(routeKey)}
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                  disableRipple
                  sx={{
                    color: 'text.red',
                    borderColor: 'text.red',
                    '&:hover': {
                      backgroundColor: 'background.red',
                      color: 'text.white',
                      borderColor: 'text.red',
                    },
                  }}
                >
                  {t('Remove Route')}
                </Button>
              </CardActions>
            </Card>
          ))}

          <Button
            variant="outlined"
            onClick={handleAddPreferredRoute}
            startIcon={<AddIcon />}
            disableRipple
            sx={{
              mt: 2,
              color: 'text.darkgrey',
              borderColor: 'border.default',
              '&:hover': {
                backgroundColor: 'background.shadowBlack',
                color: 'text.white',
                borderColor: 'background.shadowBlack',
              },
            }}
          >
            {t('Add New Route')}
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <DestinationsTab
          preferredDestinations={form.preferredDestinations}
          returnDestinations={form.returnDestinations}
          destinationsToExclude={form.destinationsToExclude}
          onPreferredDestinationsChange={(destinations: Record<string, Destination[]>) => setForm(prev => ({ ...prev, preferredDestinations: destinations }))
          }
          onReturnDestinationsChange={(destinations: Record<string, Destination[]>) => setForm(prev => ({ ...prev, returnDestinations: destinations }))
          }
          onDestinationsToExcludeChange={(destinations: Destination[]) => setForm(prev => ({ ...prev, destinationsToExclude: destinations }))
          }
        />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Working Countries')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Select the countries where your company operates. Auto-search will look for offers with pickup or delivery locations in ONLY the selected countries. To manage the large number of available countries, select only the regions where you actively operate to keep search results relevant and focused.')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('Tip: Start with your primary operating countries. You can always add more later if you expand your service area.')}
          </Alert>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1
        }}>
          {COUNTRY_OPTIONS.map((country) => {
            const isChecked = form.workingCountries?.includes(country.code) || false;

            return (
              <FormControlLabel
                key={country.code}
                control={
                  <Switch
                    checked={isChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm(prev => {
                        const currentCountries = prev.workingCountries || [];
                        const updatedCountries = checked
                          ? [...currentCountries, country.code]
                          : currentCountries.filter(c => c !== country.code);

                        return {
                          ...prev,
                          workingCountries: updatedCountries
                        };
                      });
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: isChecked ? 600 : 400 }}>
                    {country.code} - {country.name}
                  </Typography>
                }
              />
            );
          })}
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <ScoringTab
          scoringWeights={form.scoringWeights}
          onWeightsChange={handleScoringWeightsChange}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <EmailTab
          type="tenant"
          form={form}
          onChange={handleChange}
          onChangeEmailSignaturePlain={handleChange}
          onChangeEmailSignatureHtml={handleChange}
          onLinkEmail={linkMail}
          smtpSource={form?.smtpSource}
          imapSource={form?.imapSource}
          emailAddress={form?.workingEmail}
          variant="textinput"
        />
      </TabPanel>

      {/* Email Templates */}
      <TabPanel value={activeTab} index={8}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('Email Templates')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('Start and accept negotiation templates used by AI.')}
        </Typography>

        {/* start negotiation samples */}
        <CustomAccordion
          title={t('Start Negotiation')}
          description={t('Template used when starting a negotiation with a carrier.')}
        >
          <Box sx={{ mb: 2 }}>
            {form.startNegoSamples.map((sample, index) => (
              <Box>
                <Stack direction={'row'} alignItems="center" justifyContent="space-between" spacing={2} key={index}>
                  <Typography variant="subtitle2" sx={{ minWidth: 60 }}>
                    {t('Sample')} {index + 1}:
                  </Typography>
                  <IconButton
                    onClick={() => setForm(prev => {
                      const updatedSamples = [...(prev.startNegoSamples || [])];
                      updatedSamples.splice(index, 1);
                      return { ...prev, startNegoSamples: updatedSamples };
                    })}
                    color="error"
                    size="small"
                    disableRipple
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <TextField
                  fullWidth
                  key={index}
                  multiline
                  minRows={4}
                  maxRows={8}
                  value={sample}
                  onChange={(e) => setForm(prev => {
                    const updatedSamples = [...(prev.startNegoSamples || [])];
                    updatedSamples[index] = e.target.value;
                    return { ...prev, startNegoSamples: updatedSamples };
                  })}
                  sx={{ mb: 3 }}
                />
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            onClick={() => setForm(prev => ({
              ...prev,
              startNegoSamples: [...(prev.startNegoSamples || []), '']
            }))}
            startIcon={<AddIcon />}
            variant="outlined"
            disableRipple
            sx={{
              color: 'text.darkgrey',
              borderColor: 'border.default',
              '&:hover': {
                backgroundColor: 'background.shadowBlack',
                color: 'text.white',
                borderColor: 'border.default',
              },
            }}
          >
            {t('Add Item')}
          </Button>
        </CustomAccordion>

        {/* accept negotiation samples */}
        <CustomAccordion
          title={t('Accept Negotiation')}
          description={t('Template used when accepting a negotiation with a carrier.')}
        >
          <Box sx={{ mb: 2 }}>
            {form.acceptNegoSamples.map((sample, index) => (
              <Box>
                <Stack direction={'row'} alignItems="center" justifyContent="space-between" spacing={2} key={index}>
                  <Typography variant="subtitle2" sx={{ minWidth: 60 }}>
                    {t('Sample')} {index + 1}:
                  </Typography>
                  <IconButton
                    onClick={() => setForm(prev => {
                      const updatedSamples = [...(prev.acceptNegoSamples || [])];
                      updatedSamples.splice(index, 1);
                      return { ...prev, acceptNegoSamples: updatedSamples };
                    })}
                    color="error"
                    size="small"
                    disableRipple
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <TextField
                  fullWidth
                  key={index}
                  multiline
                  minRows={4}
                  maxRows={8}
                  value={sample}
                  onChange={(e) => setForm(prev => {
                    const updatedSamples = [...(prev.acceptNegoSamples || [])];
                    updatedSamples[index] = e.target.value;
                    return { ...prev, acceptNegoSamples: updatedSamples };
                  })}
                  sx={{ mb: 3 }}
                />
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            onClick={() => setForm(prev => ({
              ...prev,
              acceptNegoSamples: [...(prev.acceptNegoSamples || []), '']
            }))}
            startIcon={<AddIcon />}
            variant="outlined"
            disableRipple
            sx={{
              color: 'text.darkgrey',
              borderColor: 'border.default',
              '&:hover': {
                backgroundColor: 'background.shadowBlack',
                color: 'text.white',
                borderColor: 'border.default',
              },
            }}
          >
            {t('Add Item')}
          </Button>
        </CustomAccordion>
      </TabPanel>

      <TabPanel value={activeTab} index={9}>
        {renderPlaceholderTab(t('Notification Settings'), t('Your notification settings will appear here soon.'))}
      </TabPanel>

      <TabPanel value={activeTab} index={10}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Integration Settings')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Manage external integrations and marketplaces. Configure Cartrack for vehicle import and enable marketplaces like Timocom and Wtransnet for offer search.')}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            {t('Cartrack Integration')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Configure your Cartrack credentials to import vehicles from your Cartrack account. Credentials are required before you can use the 'Import Trucks' feature.")}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <TextInput
                fullWidth
                label={t('Cartrack Host')}
                value={form.pluginConfig?.CartrackTracker?.host ?? ''}
                onChange={(e) => handlePluginConfigChange('CartrackTracker', 'host', e.target.value)}
                helperText={t('Cartrack API host URL (e.g., https://api.cartrack.com)')}
              />
            </Box>

            <Box>
              <TextInput
                fullWidth
                label={t('Cartrack Username')}
                value={form.pluginConfig?.CartrackTracker?.username ?? ''}
                onChange={(e) => handlePluginConfigChange('CartrackTracker', 'username', e.target.value)}
                helperText={t('Your Cartrack account username')}
              />
            </Box>

            <Box>
              <TextInput
                fullWidth
                label={t('Cartrack Password')}
                type="password"
                value={form.pluginConfig?.CartrackTracker?.password === null ? '' : (form.pluginConfig?.CartrackTracker?.password ?? '')}
                onChange={(e) => handlePluginConfigChange('CartrackTracker', 'password', e.target.value)}
                helperText={t('Your Cartrack account password')}
                placeholder={form.pluginConfig?.CartrackTracker?.password === null ? t('Configured') : undefined}
                slotProps={form.pluginConfig?.CartrackTracker?.password === null ? { inputLabel: { shrink: true } } : undefined}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.warning', borderRadius: 1, border: '1px solid text.warningOrange' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('Note:')}</strong> {t("After saving your Cartrack credentials, you can use the 'Import Trucks' button on the monitoring page to import vehicles from your Cartrack account.")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            {t('Enabled Marketplaces')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Enable marketplaces to use them in offer search, automations, and future integrations.')}
          </Typography>

          <Autocomplete
            multiple
            options={[
              MarketplaceNameEnum.TimocomMarketplace,
              MarketplaceNameEnum.WtransnetMarketplace,
              MarketplaceNameEnum.LKWWalterMarketplace,
              MarketplaceNameEnum.TransEUMarketplace,
              MarketplaceNameEnum.TelerouteMarketplace
            ]}
            value={form.enabledMarketplaces || []}
            onChange={(_, newValue) => {
              setForm(prev => ({ ...prev, enabledMarketplaces: newValue }));
            }}
            getOptionLabel={(option) => {
              switch (option) {
                case MarketplaceNameEnum.TimocomMarketplace:
                  return t('Timocom Marketplace');
                case MarketplaceNameEnum.WtransnetMarketplace:
                  return t('Wtransnet Marketplace');
                case MarketplaceNameEnum.LKWWalterMarketplace:
                  return t('LKW Walter Marketplace');
                case MarketplaceNameEnum.TransEUMarketplace:
                  return t('Trans.EU Marketplace');
                case MarketplaceNameEnum.TelerouteMarketplace:
                  return t('Teleroute Marketplace');
                default:
                  return t('Unknown Marketplace');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Select Marketplaces')}
                placeholder={t('Choose marketplaces...')}
                helperText={t('Select one or more marketplaces to enable')}
              />
            )}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                label={
                  option === MarketplaceNameEnum.TimocomMarketplace
                    ? t('Timocom Marketplace')
                    : option === MarketplaceNameEnum.WtransnetMarketplace
                      ? t('Wtransnet Marketplace')
                      : option === MarketplaceNameEnum.LKWWalterMarketplace
                        ? t('LKW Walter Marketplace')
                        : option === MarketplaceNameEnum.TransEUMarketplace
                          ? t('Trans.EU Marketplace')
                          : option === MarketplaceNameEnum.TelerouteMarketplace
                            ? t('Teleroute Marketplace')
                            : t('Unknown Marketplace')
                }
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: 'background.shadowBlack',
                  color: 'text.white',
                  '& .MuiChip-deleteIcon': {
                    color: 'text.white',
                    '&:hover': {
                      color: 'text.lightGrey',
                    },
                  },
                }}
              />
            ))
            }
            sx={{ mb: 3 }}
          />
        </Box>

        {(form.enabledMarketplaces || []).includes(MarketplaceNameEnum.TimocomMarketplace) && (
          <Card sx={{ mb: 4, border: '1px solid border.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Timocom Integration')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextInput
                    fullWidth
                    label={t('Timocom Username')}
                    value={form.pluginConfig?.TimocomMarketplace?.username ?? ''}
                    onChange={(e) => handlePluginConfigChange('TimocomMarketplace', 'username', e.target.value)}
                    helperText={t('Your Timocom account username')}
                  />
                  <TextInput
                    fullWidth
                    label={t('Timocom Password')}
                    type="password"
                    value={form.pluginConfig?.TimocomMarketplace?.password === null ? '' : (form.pluginConfig?.TimocomMarketplace?.password ?? '')}
                    onChange={(e) => handlePluginConfigChange('TimocomMarketplace', 'password', e.target.value)}
                    helperText={t('Your Timocom account password')}
                    placeholder={form.pluginConfig?.TimocomMarketplace?.password === null ? t('Configured') : undefined}
                    slotProps={form.pluginConfig?.TimocomMarketplace?.password === null ? { inputLabel: { shrink: true } } : undefined}
                    sx={{ mt: 3 }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('Credentials & Limits')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('Credentials are optional. If left empty, platform-wide Timocom credentials may be used, which can be subject to rate limits.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('After saving Timocom credentials, marketplace actions that require authentication will become available.')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mt: 3 }}>
                <TextInput
                  fullWidth
                  label={t('Timocom ID')}
                  value={form.pluginConfig?.TimocomMarketplace?.timocomId ?? ''}
                  onChange={(e) => handlePluginConfigChange('TimocomMarketplace', 'timocomId', e.target.value)}
                  helperText={t('Your Timocom company ID (shown in freight exchange emails)')}
                />
                <TextInput
                  fullWidth
                  label={t('Contact Person')}
                  value={form.pluginConfig?.TimocomMarketplace?.contactPerson ?? ''}
                  onChange={(e) => handlePluginConfigChange('TimocomMarketplace', 'contactPerson', e.target.value)}
                  helperText={t('Contact person name for freight exchange emails')}
                />
                <TextInput
                  fullWidth
                  label={t('Contact Phone')}
                  value={form.pluginConfig?.TimocomMarketplace?.contactPhone ?? ''}
                  onChange={(e) => handlePluginConfigChange('TimocomMarketplace', 'contactPhone', e.target.value)}
                  helperText={t('Contact phone number for freight exchange emails')}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {(['WtransnetMarketplace', 'LKWWalterMarketplace'] as MarketplaceNameEnum[]).some(m => (form.enabledMarketplaces || []).includes(MarketplaceNameEnum[m])) && (
          <Card sx={{ mb: 4, border: '1px solid border.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Wtransnet Integration')}
                {' / '}
                {t('LKW Walter Integration')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('Credentials')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('No credentials are required for Wtransnet or LKW Walter.')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('Required Browser Plugin')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('The Chrome plugin "scrapper-wtransnet" must be installed and active when Wtransnet or LKW Walter is enabled.')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {(form.enabledMarketplaces || []).includes(MarketplaceNameEnum.TransEUMarketplace) && (
          <Card sx={{ mb: 4, border: '1px solid border.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Trans.EU Integration')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('OAuth Authentication')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('Trans.EU uses OAuth authentication. Click the button below to connect your Trans.EU account.')}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => linkMail('transEU')}
                    sx={{
                      backgroundColor: 'background.shadowBlack',
                      color: 'text.white',
                      '&:hover': {
                        backgroundColor: 'background.black',
                      },
                    }}
                  >
                    {(form.pluginConfig?.TransEUMarketplace?.refreshToken) ? t('Reconnect Trans.EU Account') : t('Connect Trans.EU Account')}
                  </Button>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('Status')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(form.pluginConfig?.TransEUMarketplace?.refreshToken)
                      ? t('Trans.EU account connected')
                      : t('Not connected - click the button to authenticate')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {(form.enabledMarketplaces || []).includes(MarketplaceNameEnum.TelerouteMarketplace) && (
          <Card sx={{ mb: 4, border: '1px solid border.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Teleroute Integration')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextInput
                    fullWidth
                    label={t('Teleroute Username')}
                    value={form.pluginConfig?.TelerouteMarketplace?.username ?? ''}
                    onChange={(e) => handlePluginConfigChange('TelerouteMarketplace', 'username', e.target.value)}
                    helperText={t('Your Teleroute account username')}
                  />
                  <TextInput
                    fullWidth
                    label={t('Teleroute Password')}
                    type="password"
                    value={form.pluginConfig?.TelerouteMarketplace?.password === null ? '' : (form.pluginConfig?.TelerouteMarketplace?.password ?? '')}
                    onChange={(e) => handlePluginConfigChange('TelerouteMarketplace', 'password', e.target.value)}
                    helperText={t('Your Teleroute account password')}
                    placeholder={form.pluginConfig?.TelerouteMarketplace?.password === null ? t('Configured') : undefined}
                    slotProps={form.pluginConfig?.TelerouteMarketplace?.password === null ? { inputLabel: { shrink: true } } : undefined}
                    sx={{ mt: 3 }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('Required Credentials')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('Credentials are required to use the Teleroute marketplace. You must provide your Teleroute account username and password.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('After saving your Teleroute credentials, marketplace actions will become available for automated offer search and negotiations.')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Negotiation Strategies')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Configure automated negotiation strategies for your tenant. These strategies will be applied during automated negotiations with customers.')}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            {t('Active Strategies')}
          </Typography>

          <Autocomplete
            multiple
            options={[TenantNegotiationStrategy.ENFORCE_CUSTOMER_PRICE_OFFER]}
            value={form.negotiationStrategies || []}
            onChange={(_, newValue) => {
              setForm(prev => ({ ...prev, negotiationStrategies: newValue }));
            }}
            getOptionLabel={(option) => {
              switch (option) {
                case TenantNegotiationStrategy.ENFORCE_CUSTOMER_PRICE_OFFER:
                  return t('Enforce Customer Price Offer');
                default:
                  return t('Unknown Strategy');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Select Negotiation Strategies')}
                placeholder={t('Choose strategies...')}
                helperText={t('Select one or more strategies to apply during negotiations')}
              />
            )}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                label={
                  option === TenantNegotiationStrategy.ENFORCE_CUSTOMER_PRICE_OFFER
                    ? t('Enforce Customer Price Offer')
                    : t('Unknown Strategy')
                }
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: 'background.shadowBlack',
                  color: 'text.white',
                  '& .MuiChip-deleteIcon': {
                    color: 'text.white',
                    '&:hover': {
                      color: 'text.lightGrey',
                    },
                  },
                }}
              />
            ))
            }
            sx={{ mb: 3 }}
          />

          <Box sx={{ p: 2, backgroundColor: 'background.grey', borderRadius: 1, border: '1px solid', borderColor: 'border.default' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {t('Strategy Descriptions')}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.darkGrey' }}>
                {t('Enforce Customer Price Offer')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('When enabled, the system will enforce that the customer gives his price during the negotiation process and will keep asking for a price until received, rather than suggesting its own price if no offer is initially received.')}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              {t('Note: These strategies are applied automatically during AI-powered negotiations. Make sure to test them in a controlled environment before enabling in production.')}
            </Alert>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={11}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Loading Sheet Template')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Create and customize your loading sheet document template. Use {{}} placeholders to insert dynamic booking and driver information.')}
          </Typography>
        </Box>

        {/* Template Cards Row – up to 3 per row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'stretch' }}>
          <Box sx={{ flex: '1 1 0', minWidth: 260 }}>
            <DefaultLoadingSheetTemplate
              onApplyDefault={(template) => {
                setForm(prev => ({ ...prev, bookingFileHtmlTemplate: template }));
                toast.success(t('Default template applied successfully'));
              }}
              companyName={form.name}
              brandingName={form.brandingName}
              brandingLogoUrl={form.brandingLogoUrl}
              regCountry={form.regCountry}
            />
          </Box>

          <Box sx={{ flex: '1 1 0', minWidth: 260 }}>
            <OrdemTransporteTemplate
              onApplyDefault={(template) => {
                setForm(prev => ({ ...prev, bookingFileHtmlTemplate: template }));
                toast.success(t('Default template applied successfully'));
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            {t('Template Editor')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Available placeholders:')}
            <strong> {'{{'}bookingId{'}}'}, {'{{'}vehiclePlate{'}}'}, {'{{'}driverName{'}}'}, {'{{'}driverEmail{'}}'}, {'{{'}startAddress{'}}'}, {'{{'}endAddress{'}}'}, {'{{'}startTime{'}}'}, {'{{'}endTime{'}}'}, {'{{'}price{'}}'}, {'{{'}distance{'}}'}, {'{{'}status{'}}'}, {'{{'}notificationEmail{'}}'}, {'{{'}customerDomain{'}}'}, {'{{'}externalOfferId{'}}'}</strong>
          </Typography>

          <TextField
            multiline
            rows={20}
            fullWidth
            value={form.bookingFileHtmlTemplate || ''}
            onChange={(e) => setForm(prev => ({ ...prev, bookingFileHtmlTemplate: e.target.value }))}
            placeholder={t('Enter HTML template here...')}
            helperText={t('Use HTML to design your loading sheet. Placeholders will be replaced with actual booking data when generating documents.')}
            sx={{
              mb: 3,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          {form.bookingFileHtmlTemplate && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Template Preview (Sample Data)')}
              </Typography>
              <Box
                component="iframe"
                sandbox="allow-same-origin"
                srcDoc={form.bookingFileHtmlTemplate
                  .replace(/\{\{bookingId\}\}/g, '12345')
                  .replace(/\{\{vehiclePlate\}\}/g, 'ABC-123')
                  .replace(/\{\{driverName\}\}/g, 'John Doe')
                  .replace(/\{\{driverEmail\}\}/g, 'john.doe@example.com')
                  .replace(/\{\{startAddress\}\}/g, 'Berlin, Germany')
                  .replace(/\{\{endAddress\}\}/g, 'Paris, France')
                  .replace(/\{\{startTime\}\}/g, new Date().toLocaleString())
                  .replace(/\{\{endTime\}\}/g, new Date(Date.now() + 86400000).toLocaleString())
                  .replace(/\{\{price\}\}/g, '€1,250.00')
                  .replace(/\{\{distance\}\}/g, '1,050 km')
                  .replace(/\{\{status\}\}/g, 'Confirmed')
                  .replace(/\{\{notificationEmail\}\}/g, form.notificationEmail || 'contact@company.com')
                  .replace(/\{\{customerDomain\}\}/g, 'example.com')
                  .replace(/\{\{externalOfferId\}\}/g, 'EXT-98765')
                }
                sx={{
                  width: '100%',
                  minHeight: 500,
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: 1,
                  bgcolor: 'background.white',
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setForm(prev => ({ ...prev, bookingFileHtmlTemplate: '' }));
              }}
              sx={{
                borderColor: 'border.default',
                color: 'text.grey',
                '&:hover': {
                  backgroundColor: 'background.lightGrey',
                  borderColor: 'border.medium',
                },
              }}
            >
              {t('Clear Template')}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('Note: The template will be used to generate PDF loading sheets for bookings.')}
          </Typography>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={12}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {t('Subscription Management')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Manage your subscription tiers and billing settings.')}
          </Typography>
        </Box>

        {subscriptionError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {subscriptionError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* General Subscription */}
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.darkgrey' }}>
                    {t('General Subscription')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('Standard access to core features')}
                  </Typography>
                </Box>
                <Chip
                  label={subscriptionStatus.general ? t('Active') : t('Inactive')}
                  color={subscriptionStatus.general ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </CardContent>
            {!subscriptionStatus.general && (
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={isSubscriptionLoading}
                  onClick={async () => {
                    setSubscriptionError(null);
                    setIsSubscriptionLoading(true);
                    try {
                      const data = await createBillingSubscribeLink(tenantId!, 'general');
                      if (data?.result) {
                        window.location.href = data.result;
                      }
                    } catch {
                      setSubscriptionError(t('Failed to open subscription page'));
                    } finally {
                      setIsSubscriptionLoading(false);
                    }
                  }}
                  sx={{
                    backgroundColor: 'background.shadowBlack',
                    '&:hover': { backgroundColor: 'background.darkgrey' }
                  }}
                >
                  {isSubscriptionLoading ? t('Loading...') : t('Subscribe')}
                </Button>
              </CardActions>
            )}
          </Card>

          {/* Auto Search Subscription */}
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.darkgrey' }}>
                    {t('Auto Search')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('Automated search for load opportunities')}
                  </Typography>
                </Box>
                <Chip
                  label={subscriptionStatus.autoSearch ? t('Active') : t('Inactive')}
                  color={subscriptionStatus.autoSearch ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </CardContent>
            {!subscriptionStatus.autoSearch && (
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={isSubscriptionLoading}
                  onClick={async () => {
                    setSubscriptionError(null);
                    setIsSubscriptionLoading(true);
                    try {
                      const data = await createBillingSubscribeLink(tenantId!, 'autoSearch');
                      if (data?.result) {
                        window.location.href = data.result;
                      }
                    } catch {
                      setSubscriptionError(t('Failed to open subscription page'));
                    } finally {
                      setIsSubscriptionLoading(false);
                    }
                  }}
                  sx={{
                    backgroundColor: 'background.shadowBlack',
                    '&:hover': { backgroundColor: 'background.darkgrey' }
                  }}
                >
                  {isSubscriptionLoading ? t('Loading...') : t('Subscribe')}
                </Button>
              </CardActions>
            )}
          </Card>

          {/* Auto Negotiation Subscription */}
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.darkgrey' }}>
                    {t('Auto Negotiation')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('AI-powered automated negotiation with carriers')}
                  </Typography>
                </Box>
                <Chip
                  label={subscriptionStatus.autoNego ? t('Active') : t('Inactive')}
                  color={subscriptionStatus.autoNego ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </CardContent>
            {!subscriptionStatus.autoNego && (
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={isSubscriptionLoading}
                  onClick={async () => {
                    setSubscriptionError(null);
                    setIsSubscriptionLoading(true);
                    try {
                      const data = await createBillingSubscribeLink(tenantId!, 'autoNego');
                      if (data?.result) {
                        window.location.href = data.result;
                      }
                    } catch {
                      setSubscriptionError(t('Failed to open subscription page'));
                    } finally {
                      setIsSubscriptionLoading(false);
                    }
                  }}
                  sx={{
                    backgroundColor: 'background.shadowBlack',
                    '&:hover': { backgroundColor: 'background.darkgrey' }
                  }}
                >
                  {isSubscriptionLoading ? t('Loading...') : t('Subscribe')}
                </Button>
              </CardActions>
            )}
          </Card>

          {/* MCP Subscription */}
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.darkgrey' }}>
                    {t('MCP')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('Use MCP endpoints in your favorite AI tool')}
                  </Typography>
                </Box>
                <Chip
                  label={subscriptionStatus.mcp ? t('Active') : t('Inactive')}
                  color={subscriptionStatus.mcp ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </CardContent>
            {!subscriptionStatus.mcp && (
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={isSubscriptionLoading}
                  onClick={async () => {
                    setSubscriptionError(null);
                    setIsSubscriptionLoading(true);
                    try {
                      const data = await createBillingSubscribeLink(tenantId!, 'mcp');
                      if (data?.result) {
                        window.location.href = data.result;
                      }
                    } catch {
                      setSubscriptionError(t('Failed to open subscription page'));
                    } finally {
                      setIsSubscriptionLoading(false);
                    }
                  }}
                  sx={{
                    backgroundColor: 'background.shadowBlack',
                    '&:hover': { backgroundColor: 'background.darkgrey' }
                  }}
                >
                  {isSubscriptionLoading ? t('Loading...') : t('Subscribe')}
                </Button>
              </CardActions>
            )}
          </Card>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Manage your billing and payment methods')}
          </Typography>
          <Button
            variant="outlined"
            disabled={isSubscriptionLoading}
            onClick={async () => {
              setSubscriptionError(null);
              setIsSubscriptionLoading(true);
              try {
                const data = await createBillingPortalLink(tenantId!);
                if (data?.result) {
                  window.location.href = data.result;
                }
              } catch {
                setSubscriptionError(t('Failed to open billing portal'));
              } finally {
                setIsSubscriptionLoading(false);
              }
            }}
            sx={{
              borderColor: 'border.dark',
              color: 'text.darkgrey',
              '&:hover': {
                backgroundColor: 'background.shadowBlack',
                borderColor: 'border.dark',
              },
            }}
          >
            {isSubscriptionLoading ? t('Opening...') : t('Open Billing Portal')}
          </Button>
        </Box>
      </TabPanel>
    </StandardDialog>
  );
};

export default TenantSettingsModal;

const CustomAccordion = ({
  title,
  description,
  children
}: {
  title: string,
  description: string,
  children: React.ReactNode
}) => (
  <Accordion defaultExpanded sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', '&:before': { display: 'none' } }}>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        backgroundColor: 'background.grey',
        borderRadius: '8px 8px 0 0',
        '&.Mui-expanded': {
          minHeight: 56,
        },
        '& .MuiAccordionSummary-content': {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: description }} />
      {children}
    </AccordionDetails>
  </Accordion>
);
